// pages/Articles.js
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import TransitionLink from '../components/TransitionLink';
import { useArticles } from '../context/articlesConfig';
import { useWorksLoading } from '../hooks/useWorksLoading';
import { useLanguage } from '../context/LanguageContext';
import { useTranslation } from '../translations/translations';
import SEO from '../components/SEO';

const categoryColors = {
    Broadcast: 'text-orange-400 border-orange-500/30',
    Code: 'text-blue-400 border-blue-500/30',
    '3D': 'text-purple-400 border-purple-500/30',
    Personal: 'text-green-400 border-green-500/30',
};

const Articles = () => {
    const { articles } = useArticles();
    const [activeCategory, setActiveCategory] = useState('All');

    const { language } = useLanguage();
    const t = useTranslation(language);

    const categories = ['All', ...Array.from(new Set(articles.map(a => a.category)))];

    const filtered = activeCategory === 'All'
        ? articles
        : articles.filter(a => a.category === activeCategory);

    useEffect(() => {
        if (window.lenis) {
            window.lenis.scrollTo(0, { immediate: true });
        } else {
            window.scrollTo(0, 0);
        }
    }, []);

    const formatDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
    };

    return (
        <div className="min-h-screen bg-black text-white">
            <SEO
                title="Articles"
                description="Broadcast design, code, 3D workflow and personal reflections from fifteen years in Italian national television and creative development."
                url="/articles"
                type="website"
            />
            <Header isAboutDark={true} />

            {/* Hero */}
            <section className="pt-32 pb-16">
                <div className="container mx-auto px-8 md:px-16">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, ease: 'easeOut' }}
                    >
                        <h1
                            className="text-6xl md:text-8xl font-bold tracking-tight mb-6"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                        >
                            {t('blog')}
                        </h1>
                        <p className="text-xl text-gray-400 max-w-2xl leading-relaxed">
                            {t('blog_subtitle')}
                        </p>
                    </motion.div>

                    {/* Category filter */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6, delay: 0.15, ease: 'easeOut' }}
                        className="flex flex-wrap gap-3 mt-12"
                    >
                        {categories.map(cat => (
                            <button
                                key={cat}
                                onClick={() => setActiveCategory(cat)}
                                className={`px-4 py-1.5 text-sm rounded-full border transition-all duration-200 font-medium
                                    ${activeCategory === cat
                                        ? 'bg-accent text-black border-accent'
                                        : 'text-gray-400 border-gray-700 hover:border-gray-500 hover:text-white'
                                    }`}
                            >
                                {cat}
                            </button>
                        ))}
                    </motion.div>
                </div>
            </section>

            {/* Articles list */}
            <section className="pb-32">
                <div className="container mx-auto px-8 md:px-16">
                    {articles.length === 0 ? (
                        <div className="py-24 text-center">
                            <p className="text-2xl md:text-3xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                                Work in progress
                            </p>
                            <p className="text-gray-400 max-w-md mx-auto leading-relaxed">
                                I'm still writing the first articles. Check back soon.
                            </p>
                        </div>
                    ) : (
                    <div className="divide-y divide-gray-800">
                        {filtered.map((article, index) => (
                            <motion.div
                                key={article.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.5, delay: index * 0.07, ease: 'easeOut' }}
                            >
                                <TransitionLink
                                    to={`/articles/${article.id}`}
                                    className="group flex flex-col md:flex-row md:items-start gap-6 py-10 hover:opacity-90 transition-opacity"
                                >
                                    {/* Date + meta — left column on desktop */}
                                    <div className="md:w-48 flex-shrink-0">
                                        <span className="text-sm text-gray-500 font-mono">
                                            {formatDate(article.date)}
                                        </span>
                                        <div className="mt-2 flex items-center gap-3">
                                            <span className={`text-xs px-2 py-0.5 rounded border ${categoryColors[article.category] || 'text-gray-400 border-gray-600'}`}>
                                                {article.category}
                                            </span>
                                            <span className="text-xs text-gray-600">{article.readTime}</span>
                                        </div>
                                    </div>

                                    {/* Content */}
                                    <div className="flex-1">
                                        <h2
                                            className="text-2xl md:text-3xl font-bold mb-3 group-hover:text-accent transition-colors tracking-tight leading-tight"
                                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                        >
                                            {article.title}
                                        </h2>
                                        <p className="text-gray-400 leading-relaxed mb-4 max-w-2xl">
                                            {article.excerpt}
                                        </p>
                                        <div className="flex items-center gap-2 text-accent text-sm font-medium">
                                            <span>Read</span>
                                            <svg
                                                className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-200"
                                                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                                            </svg>
                                        </div>
                                    </div>
                                </TransitionLink>
                            </motion.div>
                        ))}
                    </div>
                    )}
                </div>
            </section>
        </div>
    );
};

export default Articles;
